import React from 'react';
import styles from './AccountCard.module.scss';
import { Button, ButtonProps } from '../Button/Button';

/** Non-icon-only button props for card action buttons. */
export type AccountCardAction = Omit<
  Extract<ButtonProps, { iconOnly?: false }>,
  'size'
> & {
  label: string;
};

/**
 * `default` — původní karta s akcemi (výchozí).
 * `compact` — jednořádkový zápis do tabulkového seznamu účtů: název, číslo,
 * druhá částka a zůstatek ve sloupcích, bez akčních tlačítek. Sloupce sedí
 * s {@link AccountListHeader}, který se vykresluje nad skupinou řádků.
 */
export type AccountCardVariant = 'default' | 'compact';

export interface AccountCardProps {
  title: string;
  description?: string;
  amount: string;
  unit: string;
  subvalue?: string | string[];
  actions?: AccountCardAction[];
  variant?: AccountCardVariant;
  /**
   * Druhá částka (typicky účetní zůstatek). V kompaktní variantě má vlastní
   * sloupec — na rozdíl od `subvalue` proto nese jen hodnotu, ne popisek.
   */
  secondaryAmount?: string;
  /**
   * Popisek druhé částky. Na širokém displeji ho nese hlavička sloupců, takže
   * je skrytý; na telefonu se hlavička nevykresluje a bez popisku by u řádku
   * stálo holé číslo bez kontextu.
   */
  secondaryLabel?: string;
  /** Malý štítek za názvem, např. role uživatele k účtu (vlastní/disponentský). */
  badge?: string;
  className?: string;
  'data-testid': string;
}

export const AccountCard: React.FC<AccountCardProps> = ({
  title,
  description,
  amount,
  unit,
  subvalue,
  actions,
  variant = 'default',
  secondaryAmount,
  secondaryLabel,
  badge,
  className,
  'data-testid': dataTestId,
}) => {
  if (variant === 'compact') {
    return (
      <div
        className={`${styles['compact-row']} ${className || ''}`}
        data-testid={dataTestId}
        aria-label={title}
      >
        <div className={styles['compact-title-cell']}>
          {/* `title` zpřístupní celý název i tam, kde se text ořízne */}
          <span className={styles['compact-title']} title={title}>
            {title}
          </span>
          {badge && <span className={styles['compact-badge']}>{badge}</span>}
        </div>
        <div className={styles['compact-description']} title={description}>
          {description}
        </div>
        <div className={styles['compact-secondary']}>
          {secondaryLabel && (
            <span className={styles['compact-secondary-label']}>
              {secondaryLabel}:{' '}
            </span>
          )}
          {secondaryAmount}
        </div>
        <div className={styles['compact-amount']}>
          {amount}
          {unit ? ` ${unit}` : ''}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles['account-card']} ${className || ''}`}
      data-testid={dataTestId}
      aria-label={title}
    >
      <div className={styles['content']}>
        <div className={styles['left']}>
          <div className={styles['title-row']}>
            <h2 className={styles['title']}>{title}</h2>
            {badge && <span className={styles['badge']}>{badge}</span>}
          </div>
          {description && (
            <div className={styles['description']}>{description}</div>
          )}
        </div>
        <div className={styles['right']}>
          <div className={styles['amount-row']}>
            <span className={styles['amount']}>{amount}</span>
            <span className={styles['unit']}>{unit}</span>
          </div>
          {subvalue && (
            <div className={styles['subvalue']}>
              {Array.isArray(subvalue)
                ? subvalue.map((line, idx) => <div key={idx}>{line}</div>)
                : subvalue}
            </div>
          )}
        </div>
      </div>
      {actions && actions.length > 0 && (
        <div className={styles['actions']}>
          {actions.map((action, idx) => {
            const { label, ...buttonProps } = action;
            return (
              <Button key={idx} size="sm" {...buttonProps}>
                {label}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export interface AccountListHeaderProps {
  /** Popisek prvního sloupce (název účtu / smlouvy). */
  titleLabel: string;
  /** Popisek druhého sloupce (číslo účtu / smlouvy). */
  descriptionLabel: string;
  /** Popisek sloupce s druhou částkou (účetní zůstatek). */
  secondaryLabel: string;
  /** Popisek sloupce s hlavní částkou (disponibilní zůstatek). */
  amountLabel: string;
  className?: string;
  'data-testid'?: string;
}

/**
 * Hlavička sloupců pro skupinu {@link AccountCard} v kompaktní variantě.
 * Sdílí s řádky stejnou mřížku, takže sloupce lícují.
 */
export const AccountListHeader: React.FC<AccountListHeaderProps> = ({
  titleLabel,
  descriptionLabel,
  secondaryLabel,
  amountLabel,
  className,
  'data-testid': dataTestId,
}) => (
  <div
    className={`${styles['compact-header']} ${className || ''}`}
    data-testid={dataTestId}
  >
    <div className={styles['compact-title-cell']}>{titleLabel}</div>
    <div className={styles['compact-description']}>{descriptionLabel}</div>
    <div className={styles['compact-secondary']}>{secondaryLabel}</div>
    <div className={styles['compact-amount-label']}>{amountLabel}</div>
  </div>
);
